import React, { useMemo } from 'react';
import { AnalysisResult } from '../../contracts/scoring';
import { RhythmVisualizer } from '../../components/RhythmVisualizer';
import { AdviceCard } from '../../components/SmartTrainer/AdviceCard';

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const fmt = (n: any, decimals = 0) => {
  const x = safeNum(n);
  if (x === null) return '—';
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

export const NuanceTab: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  const score = result.session.metrics.nuance || 0;
  const diagnostics = result.feedback.diagnostics.filter(d => d.domain === 'NUANCE');
  const dbg = (result.session.metrics.debug || {}) as any;

  // ✅ Durée vérité (DSP) — utilisée pour toute projection temporelle
  // 1) result.session.duration (nouveau pipeline)
  // 2) fallback legacy: result.dsp.duration (anciennes sessions)
  const durationSec = useMemo(() => {
    const d1 = safeNum((result as any)?.session?.duration);
    if (d1 !== null && d1 > 0) return d1;

    const d2 = safeNum((result as any)?.dsp?.duration);
    if (d2 !== null && d2 > 0) return d2;

    // dernier fallback: debug trace (si tu l’as)
    const d3 = safeNum((result as any)?.session?.metrics?.debug?.durationSecTruth);
    if (d3 !== null && d3 > 0) return d3;

    return undefined;
  }, [result]);

  const measuredCards: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [];

    // ✅ 1) Nuance score
    cards.push({
      label: 'Nuance score',
      value: `${fmt(score)}%`,
      hint: 'Plus haut = mieux',
    });

    // ✅ 2) RMS (si dispo)
    const rmsDb = safeNum(dbg.rms_db);
    if (rmsDb !== null) {
      cards.push({
        label: 'Average level (RMS)',
        value: `${fmt(rmsDb, 1)} dB`,
        hint: 'Niveau moyen détecté',
      });
    }

    // ✅ 3) Relative dynamics (si dispo)
    const relDyn = safeNum(dbg.nuance?.relativeDynamics);
    if (relDyn !== null) {
      cards.push({
        label: 'Relative dynamics',
        value: `${fmt(relDyn, 2)}x`,
        hint: 'Écart de dynamique vs master',
      });
    }

    // ✅ 4) Envelopes presence (preuve pipeline)
    const refEnvLen = Array.isArray(result.rhythm?.refEnvelope) ? result.rhythm!.refEnvelope.length : 0;
    const stuEnvLen = Array.isArray(result.rhythm?.stuEnvelope) ? result.rhythm!.stuEnvelope.length : 0;

    cards.push({
      label: 'Ref envelope',
      value: refEnvLen > 0 ? `${refEnvLen} pts` : '—',
      hint: 'Signature d’intensité (réf)',
    });

    cards.push({
      label: 'Stu envelope',
      value: stuEnvLen > 0 ? `${stuEnvLen} pts` : '—',
      hint: 'Signature d’intensité (élève)',
    });

    // ✅ 5) BPM (utile pour interpréter)
    cards.push({
      label: 'BPM',
      value: String(result.session?.bpm || 90),
      hint: 'Tempo analysé',
    });

    // ✅ 6) Duration (trace utile)
    cards.push({
      label: 'Duration',
      value: durationSec ? `${fmt(durationSec, 2)} s` : '—',
      hint: 'Durée session (vérité DSP)',
    });

    return cards.slice(0, 6);
  }, [dbg, result.rhythm, result.session?.bpm, score, durationSec]);

  return (
    <div className="flex flex-col gap-10 animate-fade-in pb-12">
      {/* 1) SCORE + POURQUOI + CONSEILS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">Dynamic Range</span>
          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${score > 70 ? 'text-emerald-400' : 'text-purple-400'}`}>
              {score}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col justify-center gap-6 shadow-inner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center text-2xl">🌊</div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-widest">Contrôle de l'Intensité</h4>
          </div>

          <p className="text-lg text-slate-300 italic leading-relaxed pl-4 border-l-2 border-purple-500/40">
            "La nuance n’est pas juste le volume. C’est votre capacité à créer un relief : contrôler le faible, le fort,
            et surtout la transition entre les deux — sans écraser le groove."
          </p>

          <div className="space-y-3">
            {diagnostics.length > 0 ? (
              diagnostics.map((adv, i) => <AdviceCard key={i} advice={adv} isHighContrast={false} />)
            ) : (
              <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl text-center">
                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">
                  Nuances conformes au master
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ 2) MESURES DÉTECTÉES (standard) */}
      <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Mesures détectées</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {measuredCards.map((m, i) => (
            <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{m.label}</div>
              <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
              {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* 3) MODULE (inchangé visuellement) */}
      <div className="h-[600px]">
        <RhythmVisualizer
          refEnvelope={result.rhythm?.refEnvelope || []}
          stuEnvelope={result.rhythm?.stuEnvelope || []}
          bpm={result.session?.bpm || 90}
          refUrl={result.alignedUrls?.ref}
          stuUrl={result.alignedUrls?.stu}
          durationSec={durationSec}
        />
      </div>
    </div>
  );
};
